// 連続QRフレーミング (SDQ1 — Signage Diagnostics QR framing v1)。
// スナップショット JSON が単一QR (level M 実上限 2330B) に収まらないとき、複数QRへ機械的に
// バイト分割するための転送層。QR規格の Structured Append は使わず、同じ発想を本ユースケースに
// 特化させる。スナップショット schema (signage-diagnostics.v2) 自体は変更しない。
//
// 各パートのテキストペイロード:
//
//   SDQ1|<gid>|<i>|<n>|<len>
//   <chunk>
//
// - gid : 完成JSON全体の FNV-1a ハッシュ(base36)。全パート同一。群の識別と整合検証を兼ねる。
// - i   : 1始まりのパート番号 / n: 総数 / len: 完成JSONの総バイト長。
// - chunk: JSON をコードポイント境界で均等バイト割りした i 番目の断片。固定長分割だと
//   末尾以外が常に予算いっぱいの最大密度QRになり実機で読みにくいため、必要枚数を先に
//   決めてから各枚へ均等に配る。受信側 (reassemble) は i/n/len しか見ないため、
//   チャンク長の割り方はいつでも変えられる (旧固定長分割のフレームもそのまま結合できる)。
//
// 後方互換: 単一パートに収まる場合はヘッダを付けず生JSONをそのまま返す (従来の単一QR読取が動く)。

export const FRAME_MAGIC = "SDQ1";
const HEADER_SEP = "|";

const encoder = typeof TextEncoder !== "undefined" ? new TextEncoder() : null;

export const byteLengthOf = (s: string): number => {
  if (encoder) return encoder.encode(s).length;
  return s.length;
};

// 1文字 (コードポイント) の UTF-8 バイト長。サロゲートペアは Array.from で1要素に正規化済み前提。
const codePointByteLength = (ch: string): number => {
  const cp = ch.codePointAt(0) ?? 0;
  if (cp <= 0x7f) return 1;
  if (cp <= 0x7ff) return 2;
  if (cp <= 0xffff) return 3;
  return 4;
};

// FNV-1a 32bit → base36。短い群IDを得る。
export const hash32 = (s: string): string => {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    // 32bit FNV prime 乗算 (オーバーフローを Math.imul で畳む)。
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(36);
};

export interface Frame {
  gid: string;
  i: number; // 1始まり
  n: number;
  len: number; // 完成JSONの総バイト長
  chunk: string;
}

// JSON をコードポイント境界で均等バイト割りに分割。各断片は valid UTF-8 なので
// QR byte mode の往復で壊れない。必要枚数 n = ceil(total/budget) を先に決め、
// 「残りバイト ÷ 残り枚数」を毎チャンク目標に切り直すことで、境界調整の数バイトの
// 揺れが末尾チャンクに集中せず全チャンクがほぼ同密度になる。
// マルチバイト文字が境界に重なり最終チャンクが budget を超える極端な場合のみ
// 枚数を増やしてやり直す (実データでは事実上起きない保険)。
const splitEvenlyByBytes = (json: string, totalBytes: number, budget: number): string[] => {
  for (let n = Math.ceil(totalBytes / budget); ; n += 1) {
    const chunks: string[] = [];
    let current = "";
    let currentBytes = 0;
    let remaining = totalBytes;
    let target = Math.min(budget, Math.ceil(remaining / n));
    for (const ch of json) {
      const b = codePointByteLength(ch);
      if (chunks.length < n - 1 && currentBytes + b > target && current.length > 0) {
        chunks.push(current);
        remaining -= currentBytes;
        current = "";
        currentBytes = 0;
        target = Math.min(budget, Math.ceil(remaining / (n - chunks.length)));
      }
      current += ch;
      currentBytes += b;
    }
    if (current.length > 0) chunks.push(current);
    if (currentBytes <= budget) return chunks;
  }
};

export interface FrameOptions {
  chunkBytes: number;
  maxParts: number;
}

// 完成JSONを連続QRパート群に変換する。単一パートに収まれば生JSONを1件だけ返す。
// 注: maxParts を超えるかどうかの判断・間引きは呼び出し側 (getSnapshotPartsForQR) の責務。
export const frameSnapshot = (json: string, opts: FrameOptions): string[] => {
  const len = byteLengthOf(json);
  if (len <= opts.chunkBytes) {
    return [json];
  }
  const gid = hash32(json);
  const chunks = splitEvenlyByBytes(json, len, opts.chunkBytes);
  const n = chunks.length;
  return chunks.map(
    (chunk, idx) =>
      `${FRAME_MAGIC}${HEADER_SEP}${gid}${HEADER_SEP}${idx + 1}${HEADER_SEP}${n}${HEADER_SEP}${len}\n${chunk}`,
  );
};

// テキストが SDQ1 フレームなら解析し、そうでなければ null (=単一スナップショット扱い)。
export const parseFrame = (text: string): Frame | null => {
  if (!text.startsWith(`${FRAME_MAGIC}${HEADER_SEP}`)) return null;
  const newline = text.indexOf("\n");
  if (newline < 0) return null;
  const header = text.slice(0, newline);
  const chunk = text.slice(newline + 1);
  const fields = header.split(HEADER_SEP);
  // [magic, gid, i, n, len]
  if (fields.length !== 5) return null;
  const [, gid, iStr, nStr, lenStr] = fields;
  const i = Number(iStr);
  const n = Number(nStr);
  const len = Number(lenStr);
  if (!gid || !Number.isInteger(i) || !Number.isInteger(n) || !Number.isInteger(len)) return null;
  if (i < 1 || n < 1 || i > n) return null;
  return { gid, i, n, len, chunk };
};

export interface ReassembleResult {
  ok: boolean;
  json?: string;
  error?: string;
}

// 同一 gid のフレーム群を再構成する。i 昇順に連結し、件数・総バイト長・ハッシュを検証する。
export const reassemble = (frames: Frame[]): ReassembleResult => {
  if (frames.length === 0) return { ok: false, error: "フレームがありません" };
  const { gid, n, len } = frames[0];
  if (frames.some((f) => f.gid !== gid)) {
    return { ok: false, error: "群ID (gid) が混在しています" };
  }
  if (frames.some((f) => f.n !== n || f.len !== len)) {
    return { ok: false, error: "パート総数または総バイト長が一致しません" };
  }
  const byIndex = new Map<number, string>();
  for (const f of frames) byIndex.set(f.i, f.chunk);
  if (byIndex.size !== n) {
    const missing: number[] = [];
    for (let i = 1; i <= n; i += 1) if (!byIndex.has(i)) missing.push(i);
    return { ok: false, error: `パートが不足しています (欠番: ${missing.join(", ")})` };
  }
  let json = "";
  for (let i = 1; i <= n; i += 1) json += byIndex.get(i);
  if (byteLengthOf(json) !== len) {
    return { ok: false, error: "再構成後の総バイト長が一致しません" };
  }
  if (hash32(json) !== gid) {
    return { ok: false, error: "再構成後のハッシュ (gid) が一致しません" };
  }
  return { ok: true, json };
};
